import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { travelAPI } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Plane,
  Train,
  Car,
  Bus,
  Plus,
  Trash2,
  ArrowLeft,
  Send,
  Save,
  MapPin,
  Calendar,
  IndianRupee,
  AlertTriangle,
} from 'lucide-react';

const initialItinerary = {
  leg_number: 1,
  from_location: '',
  to_location: '',
  departure_date: '',
  departure_time: '',
  return_date: '',
  return_time: '',
  mode_of_travel: 'flight',
  travel_class: 'economy',
  accommodation_required: false,
  hotel_preference: '',
  notes: '',
};

const initialCosts = {
  currency: 'INR',
  travel: 0,
  accommodation: 0,
  local_conveyance: 0,
  per_diem: 0,
  visa_fees: 0,
  insurance: 0,
  conference_registration: 0,
  other: 0,
  other_description: '',
  total_estimated: 0,
};

export default function NewTravelRequestPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [travelType, setTravelType] = useState('domestic_outstation');
  const [purpose, setPurpose] = useState('');
  const [projectCode, setProjectCode] = useState('');
  const [clientName, setClientName] = useState('');
  const [costCentre, setCostCentre] = useState('');
  const [itinerary, setItinerary] = useState([{ ...initialItinerary }]);
  const [costs, setCosts] = useState({ ...initialCosts });
  const [advanceRequired, setAdvanceRequired] = useState(false);
  const [advanceAmount, setAdvanceAmount] = useState(0);
  const [advanceJustification, setAdvanceJustification] = useState('');
  const [visaRequired, setVisaRequired] = useState(false);
  const [invitationRequired, setInvitationRequired] = useState(false);
  const [insuranceRequired, setInsuranceRequired] = useState(false);
  const [emergencyContact, setEmergencyContact] = useState({ name: '', phone: '', relationship: '' });

  const isInternational = travelType === 'international';

  const updateItinerary = (index, field, value) => {
    const newItinerary = [...itinerary];
    newItinerary[index] = { ...newItinerary[index], [field]: value };
    setItinerary(newItinerary);
  };

  const addLeg = () => {
    setItinerary([
      ...itinerary,
      { ...initialItinerary, leg_number: itinerary.length + 1 },
    ]);
  };

  const removeLeg = (index) => {
    if (itinerary.length > 1) {
      const newItinerary = itinerary.filter((_, i) => i !== index);
      newItinerary.forEach((leg, i) => (leg.leg_number = i + 1));
      setItinerary(newItinerary);
    }
  };

  const updateCost = (field, value) => {
    const newCosts = { ...costs, [field]: parseFloat(value) || 0 };
    newCosts.total_estimated =
      newCosts.travel +
      newCosts.accommodation +
      newCosts.local_conveyance +
      newCosts.per_diem +
      newCosts.visa_fees +
      newCosts.insurance +
      newCosts.conference_registration +
      newCosts.other;
    setCosts(newCosts);
  };

  const validateForm = () => {
    if (!purpose.trim()) return 'Purpose is required';
    if (itinerary.some((leg) => !leg.from_location || !leg.to_location || !leg.departure_date)) {
      return 'Please fill all required itinerary fields';
    }
    if (costs.total_estimated <= 0) return 'Please enter estimated costs';
    return null;
  };

  const handleSubmit = async (submitForApproval = false) => {
    const error = validateForm();
    if (error) {
      alert(error);
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        travel_type: travelType,
        purpose,
        project_code: projectCode || null,
        client_name: clientName || null,
        cost_centre: costCentre || null,
        itinerary: itinerary.map((leg) => ({
          ...leg,
          departure_date: new Date(leg.departure_date).toISOString(),
          return_date: leg.return_date ? new Date(leg.return_date).toISOString() : null,
        })),
        estimated_costs: costs,
        advance_request: {
          required: advanceRequired,
          amount: advanceRequired ? advanceAmount : 0,
          currency: 'INR',
          justification: advanceJustification || null,
          status: advanceRequired ? 'pending' : 'not_requested',
        },
        compliance: {
          visa_required: isInternational && visaRequired,
          visa_status: isInternational && visaRequired ? 'pending' : 'not_applicable',
          invitation_required: invitationRequired,
          invitation_received: false,
          hr_clearance_required: false,
          hr_clearance_status: 'not_applicable',
          insurance_required: isInternational && insuranceRequired,
          insurance_status: isInternational && insuranceRequired ? 'pending' : 'not_applicable',
        },
        emergency_contact: emergencyContact.name ? emergencyContact : null,
      };

      const response = await travelAPI.create(requestData);
      const requestId = response.data.id;

      if (submitForApproval) {
        await travelAPI.submit(requestId);
        alert('Travel request submitted for approval!');
      } else {
        alert('Travel request saved as draft');
      }

      navigate('/travel');
    } catch (error) {
      console.error('Error creating travel request:', error);
      alert(error.response?.data?.detail || 'Failed to create travel request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/travel')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold font-['Manrope']">New Travel Request</h1>
          <p className="text-muted-foreground">Create a new travel request for approval</p>
        </div>
      </div>

      {/* Travel Type */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Travel Type</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            {[
              { value: 'local_domestic', label: 'Local (Same City)', icon: Car },
              { value: 'domestic_outstation', label: 'Domestic Outstation', icon: Train },
              { value: 'international', label: 'International', icon: Plane },
            ].map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => setTravelType(value)}
                className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                  travelType === value
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Icon
                  className={`w-6 h-6 mx-auto mb-2 ${
                    travelType === value ? 'text-primary' : 'text-gray-400'
                  }`}
                />
                <p
                  className={`text-sm font-medium ${
                    travelType === value ? 'text-primary' : 'text-gray-600'
                  }`}
                >
                  {label}
                </p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Basic Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="purpose">Purpose of Travel *</Label>
            <Textarea
              id="purpose"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="Describe the business purpose of this travel..."
              rows={3}
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="projectCode">Project Code</Label>
              <Input
                id="projectCode"
                value={projectCode}
                onChange={(e) => setProjectCode(e.target.value)}
                placeholder="PRJ-XXX"
              />
            </div>
            <div>
              <Label htmlFor="clientName">Client Name</Label>
              <Input
                id="clientName"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Client organization"
              />
            </div>
            <div>
              <Label htmlFor="costCentre">Cost Centre</Label>
              <Input
                id="costCentre"
                value={costCentre}
                onChange={(e) => setCostCentre(e.target.value)}
                placeholder="CC-XXX"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Itinerary */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Itinerary</CardTitle>
            <CardDescription>Add your travel legs</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={addLeg}>
            <Plus className="w-4 h-4 mr-1" />
            Add Leg
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {itinerary.map((leg, index) => (
            <div
              key={index}
              className="p-4 rounded-lg border bg-muted/20 space-y-4"
            >
              <div className="flex items-center justify-between">
                <Badge variant="outline">Leg {leg.leg_number}</Badge>
                {itinerary.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => removeLeg(index)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>From *</Label>
                  <Input
                    value={leg.from_location}
                    onChange={(e) => updateItinerary(index, 'from_location', e.target.value)}
                    placeholder="City, Country"
                  />
                </div>
                <div>
                  <Label>To *</Label>
                  <Input
                    value={leg.to_location}
                    onChange={(e) => updateItinerary(index, 'to_location', e.target.value)}
                    placeholder="City, Country"
                  />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label>Departure Date *</Label>
                  <Input
                    type="date"
                    value={leg.departure_date}
                    onChange={(e) => updateItinerary(index, 'departure_date', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={leg.departure_time}
                    onChange={(e) => updateItinerary(index, 'departure_time', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Return Date</Label>
                  <Input
                    type="date"
                    value={leg.return_date}
                    onChange={(e) => updateItinerary(index, 'return_date', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={leg.return_time}
                    onChange={(e) => updateItinerary(index, 'return_time', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Mode of Travel</Label>
                  <Select
                    value={leg.mode_of_travel}
                    onValueChange={(v) => updateItinerary(index, 'mode_of_travel', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flight">Flight</SelectItem>
                      <SelectItem value="train">Train</SelectItem>
                      <SelectItem value="bus">Bus</SelectItem>
                      <SelectItem value="cab">Cab/Taxi</SelectItem>
                      <SelectItem value="self_drive">Self Drive</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Travel Class</Label>
                  <Select
                    value={leg.travel_class}
                    onValueChange={(v) => updateItinerary(index, 'travel_class', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="economy">Economy</SelectItem>
                      <SelectItem value="business">Business</SelectItem>
                      <SelectItem value="first">First Class</SelectItem>
                      <SelectItem value="sleeper">Sleeper</SelectItem>
                      <SelectItem value="ac_2tier">AC 2-Tier</SelectItem>
                      <SelectItem value="ac_3tier">AC 3-Tier</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <Switch
                    id={`accommodation-${index}`}
                    checked={leg.accommodation_required}
                    onCheckedChange={(v) => updateItinerary(index, 'accommodation_required', v)}
                  />
                  <Label htmlFor={`accommodation-${index}`}>Need Accommodation</Label>
                </div>
              </div>

              {leg.accommodation_required && (
                <div>
                  <Label>Hotel Preference</Label>
                  <Input
                    value={leg.hotel_preference}
                    onChange={(e) => updateItinerary(index, 'hotel_preference', e.target.value)}
                    placeholder="Any preferred hotel or area"
                  />
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Estimated Costs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <IndianRupee className="w-5 h-5" />
            Estimated Costs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label>Travel</Label>
              <Input
                type="number"
                value={costs.travel}
                onChange={(e) => updateCost('travel', e.target.value)}
              />
            </div>
            <div>
              <Label>Accommodation</Label>
              <Input
                type="number"
                value={costs.accommodation}
                onChange={(e) => updateCost('accommodation', e.target.value)}
              />
            </div>
            <div>
              <Label>Local Conveyance</Label>
              <Input
                type="number"
                value={costs.local_conveyance}
                onChange={(e) => updateCost('local_conveyance', e.target.value)}
              />
            </div>
            <div>
              <Label>Per Diem</Label>
              <Input
                type="number"
                value={costs.per_diem}
                onChange={(e) => updateCost('per_diem', e.target.value)}
              />
            </div>
            {isInternational && (
              <>
                <div>
                  <Label>Visa Fees</Label>
                  <Input
                    type="number"
                    value={costs.visa_fees}
                    onChange={(e) => updateCost('visa_fees', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Insurance</Label>
                  <Input
                    type="number"
                    value={costs.insurance}
                    onChange={(e) => updateCost('insurance', e.target.value)}
                  />
                </div>
              </>
            )}
            <div>
              <Label>Conference/Registration</Label>
              <Input
                type="number"
                value={costs.conference_registration}
                onChange={(e) => updateCost('conference_registration', e.target.value)}
              />
            </div>
            <div>
              <Label>Other</Label>
              <Input
                type="number"
                value={costs.other}
                onChange={(e) => updateCost('other', e.target.value)}
              />
            </div>
          </div>

          <div className="mt-6 p-4 bg-primary/5 rounded-lg flex items-center justify-between">
            <span className="font-medium">Total Estimated Cost</span>
            <span className="text-2xl font-bold text-primary">
              ₹{costs.total_estimated.toLocaleString()}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Advance Request */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Advance Request</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Switch
              id="advanceRequired"
              checked={advanceRequired}
              onCheckedChange={setAdvanceRequired}
            />
            <Label htmlFor="advanceRequired">I need advance payment</Label>
          </div>
          {advanceRequired && (
            <div className="grid grid-cols-2 gap-4 pl-8">
              <div>
                <Label>Advance Amount (INR)</Label>
                <Input
                  type="number"
                  value={advanceAmount}
                  onChange={(e) => setAdvanceAmount(parseFloat(e.target.value) || 0)}
                  max={costs.total_estimated * 0.8}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Max 80% of estimated: ₹{Math.floor(costs.total_estimated * 0.8).toLocaleString()}
                </p>
              </div>
              <div>
                <Label>Justification</Label>
                <Input
                  value={advanceJustification}
                  onChange={(e) => setAdvanceJustification(e.target.value)}
                  placeholder="Why do you need advance?"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Compliance (International) */}
      {isInternational && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Compliance Requirements
            </CardTitle>
            <CardDescription>International travel compliance checklist</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <Switch checked={visaRequired} onCheckedChange={setVisaRequired} />
              <Label>Visa Required</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={invitationRequired} onCheckedChange={setInvitationRequired} />
              <Label>Official Invitation/Conference Registration Required</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={insuranceRequired} onCheckedChange={setInsuranceRequired} />
              <Label>Travel Insurance Required</Label>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Emergency Contact */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Emergency Contact</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Name</Label>
              <Input
                value={emergencyContact.name}
                onChange={(e) => setEmergencyContact({ ...emergencyContact, name: e.target.value })}
                placeholder="Contact person name"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={emergencyContact.phone}
                onChange={(e) => setEmergencyContact({ ...emergencyContact, phone: e.target.value })}
                placeholder="+91-XXXXXXXXXX"
              />
            </div>
            <div>
              <Label>Relationship</Label>
              <Input
                value={emergencyContact.relationship}
                onChange={(e) => setEmergencyContact({ ...emergencyContact, relationship: e.target.value })}
                placeholder="e.g., Spouse, Parent"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4">
        <Button variant="outline" onClick={() => navigate('/travel')}>
          Cancel
        </Button>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => handleSubmit(false)} disabled={loading}>
            <Save className="w-4 h-4 mr-2" />
            Save as Draft
          </Button>
          <Button onClick={() => handleSubmit(true)} disabled={loading}>
            <Send className="w-4 h-4 mr-2" />
            Submit for Approval
          </Button>
        </div>
      </div>
    </div>
  );
}
